import React, { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';

const STORE_ICONS = {
    qdrant: 'M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125',
    s3_vectors: 'M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z',
};

function StatCard({ label, value, sub }) {
    return (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <p className="text-xs font-medium text-gray-500">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
    );
}

function LatencyCard({ label, stats }) {
    if (!stats || stats.count === 0) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
                <p className="text-xs font-medium text-gray-500">{label}</p>
                <p className="text-sm text-gray-400 mt-2">No data</p>
            </div>
        );
    }
    return (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <p className="text-xs font-medium text-gray-500">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">
                {stats.avg_ms.toFixed(0)}<span className="text-sm font-normal text-gray-400 ml-0.5">ms</span>
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span>min {stats.min_ms.toFixed(0)}ms</span>
                <span>max {stats.max_ms.toFixed(0)}ms</span>
                <span>{stats.count} ops</span>
            </div>
        </div>
    );
}

function StoreCard({ store }) {
    const isConnected = store.status === 'connected';
    const icon = STORE_ICONS[store.provider] || STORE_ICONS.qdrant;

    return (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isConnected ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <svg className={`w-4 h-4 ${isConnected ? 'text-emerald-600' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                    </svg>
                </div>
                <div>
                    <h4 className="text-sm font-medium text-gray-900 capitalize">{store.provider?.replace('_', ' ')}</h4>
                    <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span className={`text-xs ${isConnected ? 'text-emerald-600' : 'text-red-500'}`}>
                            {isConnected ? 'Connected' : store.status}
                        </span>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 rounded px-2.5 py-1.5">
                    <span className="text-gray-500">Indexes</span>
                    <p className="font-medium text-gray-900">{store.total_indexes}</p>
                </div>
                <div className="bg-gray-50 rounded px-2.5 py-1.5">
                    <span className="text-gray-500">Vectors</span>
                    <p className="font-medium text-gray-900">{store.total_vectors >= 0 ? store.total_vectors.toLocaleString() : 'N/A'}</p>
                </div>
                {store.bucket && (
                    <div className="col-span-2 bg-gray-50 rounded px-2.5 py-1.5">
                        <span className="text-gray-500">Bucket</span>
                        <p className="font-medium text-gray-900 truncate">{store.bucket}</p>
                    </div>
                )}
                {store.region && (
                    <div className="col-span-2 bg-gray-50 rounded px-2.5 py-1.5">
                        <span className="text-gray-500">Region</span>
                        <p className="font-medium text-gray-900">{store.region}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AdminRAGOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => { loadOverview(); }, []);

    const loadOverview = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await adminApi.getRAGOverview();
            setData(res);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading overview..." center />;

    if (error) {
        return (
            <div>
                <Alert variant="error" className="mb-4">{error}</Alert>
                <Button size="sm" onClick={loadOverview}>Retry</Button>
            </div>
        );
    }

    if (!data) return null;

    const { collections, documents, vector_stores, pricing, latency, cache, totals, errors } = data;
    const totalErrors = Object.values(errors).reduce((sum, v) => sum + v, 0);
    const cacheTotal = cache.hits + cache.misses;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">RAG System Overview</h1>
                    <p className="text-sm text-gray-500 mt-0.5">System health and performance metrics</p>
                </div>
                <Button variant="ghost" size="sm" onClick={loadOverview}>Refresh</Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Collections" value={collections.total} sub={`${collections.active} active`} />
                <StatCard label="Documents" value={documents.total} sub={`${documents.indexed} indexed`} />
                <StatCard label="Total Chunks" value={documents.total_chunks.toLocaleString()} />
                <StatCard label="Searches" value={totals.searches_performed} />
            </div>

            {vector_stores.length > 0 && (
                <div>
                    <h2 className="text-sm font-medium text-gray-700 mb-3">Vector Stores</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {vector_stores.map((store, i) => (
                            <StoreCard key={store.provider || i} store={store} />
                        ))}
                    </div>
                </div>
            )}

            <div>
                <h2 className="text-sm font-medium text-gray-700 mb-3">Response Time</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <LatencyCard label="Embedding" stats={latency.embedding} />
                    <LatencyCard label="Search" stats={latency.search} />
                    <LatencyCard label="Index" stats={latency.index} />
                    <LatencyCard label="Rerank" stats={latency.rerank} />
                </div>
            </div>

            {cacheTotal > 0 && (
                <div>
                    <h2 className="text-sm font-medium text-gray-700 mb-3">Embedding Cache</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        <StatCard label="Hit Rate" value={`${(cache.hit_rate * 100).toFixed(1)}%`} />
                        <StatCard label="Hits" value={cache.hits.toLocaleString()} />
                        <StatCard label="Misses" value={cache.misses.toLocaleString()} />
                    </div>
                </div>
            )}

            <div>
                <h2 className="text-sm font-medium text-gray-700 mb-3">Embedding Model Pricing</h2>
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Model</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Provider</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Type</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500">Price / 1M tokens</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {pricing.map((m) => (
                                <tr key={m.model} className="hover:bg-gray-50">
                                    <td className="px-4 py-2.5 font-mono text-xs text-gray-900">{m.model}</td>
                                    <td className="px-3 py-2.5 text-xs text-gray-600 capitalize">{m.provider}</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            m.type === 'local'
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'bg-amber-50 text-amber-700'
                                        }`}>
                                            {m.type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right text-xs font-medium">
                                        {m.price_per_1m === 0 ? (
                                            <span className="text-emerald-600">Free</span>
                                        ) : (
                                            <span className="text-gray-900">${m.price_per_1m}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalErrors > 0 && (
                <div>
                    <h2 className="text-sm font-medium text-red-600 mb-3">Errors</h2>
                    <div className="border border-red-200 rounded-lg bg-red-50 p-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {Object.entries(errors).map(([op, count]) => (
                                <div key={op} className="text-center">
                                    <p className="text-lg font-semibold text-red-700">{count}</p>
                                    <p className="text-xs text-red-500 capitalize">{op}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
